import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  ArrowLeft, Save, Send, CheckCircle2, XCircle, BookCheck,
  RotateCcw, Ban, Trash2, AlertTriangle, CheckCircle,
  FileText, History, Paperclip, ShieldCheck, Search, X, Printer,
  ExternalLink, Plus, Landmark,
} from 'lucide-react';

import { bankDepositApi } from '@/api/bankDeposit.api';
import { bankAccountApi } from '@/api/bank.api';
import { chequeApi } from '@/api/cheque.api';
import type { ChequeRecordListDto } from '@/types/cheque.types';
import { accountApi } from '@/api/account.api';
import { exchangeRateApi } from '@/api/exchangeRate.api';
import { MasterReportPreview } from '@/components/master/report/MasterReportPreview';
import { BankDepositVoucher } from '@/components/banking/BankDepositVoucher';
import { exportToExcel } from '@/utils/reportExport';
import { usePermission } from '@/hooks/usePermission';
import { Permissions } from '@/constants/permissions';
import { AppRoutes } from '@/constants/routes';
import { Spinner } from '@/components/ui/Spinner';
import { MasterSearchModal } from '@/components/master/MasterSearchModal';
import { ERPContextBar } from '@/components/erp/ERPContextBar';
import { AccountLookupField } from '@/components/erp/AccountLookupField';
import { WorkflowStatusPanel } from '@/components/erp/WorkflowStatusPanel';
import { AttachmentPanel } from '@/components/erp/AttachmentPanel';
import { AuditViewer } from '@/components/erp/AuditViewer';
import { setErpContext, clearErpContext } from '@/state/erpContextSlice';
import { cn } from '@/utils/cn';

import type { RootState } from '@/state/store';
import { costCentresForLocation } from '@/utils/costCentreScope';
import type { SearchColumn } from '@/components/master/MasterSearchModal';
import type { BankAccountListDto } from '@/types/bank.types';
import type { AccountListDto } from '@/types/account.types';
import type {
  BankDepositDto,
  BankDepositWorkflowStatus,
  BankDepositType,
  BankDepositLineRequest,
  CreateBankDepositRequest,
  UpdateBankDepositRequest,
  CashDueRowDto,
} from '@/types/bankDeposit.types';
import { BNK_DEPOSIT_WORKFLOW_STATUS_COLORS } from '@/types/bankDeposit.types';

// ── Constants ──────────────────────────────────────────────────────────────────

const DEPOSIT_TYPE_OPTIONS: { value: BankDepositType; label: string }[] = [
  { value: 'Cash',   label: 'Cash'   },
  { value: 'Cheque', label: 'Cheque' },
  { value: 'Mixed',  label: 'Mixed'  },
];

const PAYMENT_METHOD_OPTIONS = ['Cash', 'Cheque', 'BankTransfer', 'OnlineBanking', 'Other'];

// ── Line type ──────────────────────────────────────────────────────────────────

interface DepositLineRow {
  key:                 string;
  lineNumber:          number;
  description:         string;
  depositSource:       string;
  sourceDocumentNumber: string;
  /** The cash/till GL account this line draws from (GAP-016 cash half). Posting credits it, so a
   *  deposit emptying two tills credits each till, not one lump contra. */
  sourceGlAccountId:   string;
  sourceGlAccountCode: string;
  sourceGlAccountName: string;
  paymentMethod:       string;
  chequeNumber:        string;
  chequeDate:          string;
  /** The register row this line deposits (GAP-016). Set by the cheque picker; drives the
   *  Received → Deposited transition on post. */
  chequeRecordId:      string;
  customerId:          string;
  customerCode:        string;
  amount:              number;
  /** The cash-due (location, date) row this line settles (GAP-016). Set by the due picker; the
   *  code/name are a local display cache — not persisted, and blank again on a fresh page load
   *  until the operator re-opens the picker or the amount is edited by hand. */
  sourceLocationId:    string;
  sourceBusinessDate:  string;
  sourceLocationCode:  string;
  sourceLocationName:  string;
  /** The cost centre whose cash this line banks, when it came from the Cash Deposit screen.
   *  Not editable here; kept so saving the deposit again does not drop it. */
  sourceCostCenterId:  string;
}

const mkLine = (n: number): DepositLineRow => ({
  key:                 `new-${Date.now()}-${n}-${Math.random()}`,
  lineNumber:          n,
  description:         '',
  depositSource:       '',
  sourceDocumentNumber: '',
  sourceGlAccountId:   '',
  sourceGlAccountCode: '',
  sourceGlAccountName: '',
  paymentMethod:       'Cash',
  chequeNumber:        '',
  chequeDate:          '',
  chequeRecordId:      '',
  customerId:          '',
  customerCode:        '',
  amount:              0,
  sourceLocationId:    '',
  sourceBusinessDate:  '',
  sourceLocationCode:  '',
  sourceLocationName:  '',
  sourceCostCenterId:  '',
});

// ── Tabs ───────────────────────────────────────────────────────────────────────

const TABS = [
  { key: 'lines',       label: 'Deposit Lines', icon: FileText    },
  { key: 'workflow',    label: 'Workflow',       icon: History     },
  { key: 'attachments', label: 'Attachments',    icon: Paperclip   },
  { key: 'audit',       label: 'Audit Log',      icon: ShieldCheck },
] as const;
type Tab = (typeof TABS)[number]['key'];

// ── Search columns ─────────────────────────────────────────────────────────────

const BANK_ACCOUNT_COLUMNS: SearchColumn<BankAccountListDto>[] = [
  { key: 'accountNumber', label: 'Account No.', className: 'w-32 font-mono text-teal-700 dark:text-teal-400' },
  { key: 'accountName',   label: 'Account Name' },
  { key: 'currencyCode',  label: 'CCY', className: 'w-14 font-mono text-slate-500 dark:text-gray-400' },
  { key: 'accountType',   label: 'Type', className: 'w-20' },
];

const GL_ACCOUNT_COLUMNS: SearchColumn<AccountListDto>[] = [
  { key: 'code',        label: 'Code',         className: 'w-28 font-mono text-teal-700 dark:text-teal-400' },
  { key: 'name',        label: 'Account Name' },
  { key: 'accountType', label: 'Type',         className: 'w-24' },
];

// ── Action modal ───────────────────────────────────────────────────────────────

interface ActionModalProps {
  title:       string;
  placeholder: string;
  required:    boolean;
  onConfirm:   (text: string) => void;
  onCancel:    () => void;
}

const ActionModal = ({ title, placeholder, required, onConfirm, onCancel }: ActionModalProps) => {
  const [text, setText] = useState('');
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => { ref.current?.focus(); }, []);
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-md p-6">
        <h3 className="text-base font-semibold text-slate-900 dark:text-white mb-3">{title}</h3>
        <textarea ref={ref} value={text} onChange={e => setText(e.target.value)}
          placeholder={placeholder} rows={4}
          className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm
                     bg-white dark:bg-slate-700 text-slate-900 dark:text-white resize-none
                     focus:ring-2 focus:ring-teal-500 outline-none"
        />
        <div className="flex gap-3 mt-4 justify-end">
          <button onClick={onCancel}
            className="px-4 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600
                       hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300">
            Cancel
          </button>
          <button onClick={() => onConfirm(text)} disabled={required && !text.trim()}
            className="px-4 py-2 text-sm rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50">
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

const fieldClass = (disabled: boolean) => cn(
  'w-full px-3 py-2 text-sm border rounded-lg outline-none transition-colors',
  'bg-white dark:bg-slate-700 text-slate-900 dark:text-white',
  'border-slate-200 dark:border-slate-600',
  disabled
    ? 'bg-slate-50 dark:bg-slate-800 text-slate-500 cursor-default'
    : 'focus:ring-2 focus:ring-teal-500 focus:border-transparent',
);

const fmt = (n: number) => n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ── Main Component ─────────────────────────────────────────────────────────────

export const BankDepositFormPage = () => {
  const { id }   = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isNew    = !id;

  const { hasPermission } = usePermission();
  const canEdit    = hasPermission(Permissions.BNK_DEPOSIT_EDIT);
  const canSubmit  = hasPermission(Permissions.BNK_DEPOSIT_SUBMIT);
  const canApprove = hasPermission(Permissions.BNK_DEPOSIT_APPROVE);
  const canReject  = hasPermission(Permissions.BNK_DEPOSIT_REJECT);
  const canPost    = hasPermission(Permissions.BNK_DEPOSIT_POST);
  const canReverse = hasPermission(Permissions.BNK_DEPOSIT_REVERSE);
  const canCancel  = hasPermission(Permissions.BNK_DEPOSIT_CANCEL);
  const canDelete  = hasPermission(Permissions.BNK_DEPOSIT_DELETE);
  const canPrint   = hasPermission(Permissions.BNK_DEPOSIT_PRINT);
  const canAddDoc  = hasPermission(Permissions.DOC_ATTACHMENT_ADD);
  const canDelDoc  = hasPermission(Permissions.DOC_ATTACHMENT_DELETE);

  const authUser   = useSelector((s: RootState) => s.auth.user);
  const session    = useSelector((s: RootState) => s.session.session);
  const accessibleCostCenters = useSelector((s: RootState) => s.auth.accessibleCostCenters);

  const today = new Date().toISOString().split('T')[0];
  const baseCurrency = authUser?.baseCurrencyCode ?? session?.companyCurrency ?? 'USD';

  // ── Header state ──────────────────────────────────────────────────────────
  const [depositDate,   setDepositDate]   = useState(today);
  const [postingDate,   setPostingDate]   = useState(today);
  const [bankAccountId, setBankAccountId] = useState('');
  // The line whose cheque we are picking (GAP-016), and the in-hand cheques offered.
  const [pickerForLine, setPickerForLine] = useState<string | null>(null);
  const [duePickerForLine, setDuePickerForLine] = useState<string | null>(null);
  const [bankAccountNumber, setBankAccountNumber] = useState('');
  const [bankAccountName,   setBankAccountName]   = useState('');
  const [bankAccountCode,   setBankAccountCode]   = useState('');
  const [depositType,   setDepositType]   = useState<BankDepositType>('Cash');
  const [currencyCode,  setCurrencyCode]  = useState(baseCurrency);
  const [exchangeRate,  setExchangeRate]  = useState(1);
  const [description,   setDescription]   = useState('');
  const [reference,     setReference]     = useState('');
  const [costCenterId,  setCostCenterId]  = useState('');

  const [bankOpen,   setBankOpen]   = useState(false);
  const [contraOpen, setContraOpen] = useState(false);
  const [contraAccountId,   setContraAccountId]   = useState('');
  const [contraAccountCode, setContraAccountCode] = useState('');
  const [contraAccountName, setContraAccountName] = useState('');

  // ── Lines ──────────────────────────────────────────────────────────────────
  const [lines, setLines] = useState<DepositLineRow[]>([mkLine(1)]);

  const updateLine = (key: string, patch: Partial<DepositLineRow>) =>
    setLines(ls => ls.map(l => l.key === key ? { ...l, ...patch } : l));

  const addLine = () => setLines(ls => [...ls, mkLine(ls.length + 1)]);
  const removeLine = (key: string) => setLines(ls => ls.filter(l => l.key !== key));

  const totalAmount = lines.reduce((s, l) => s + (l.amount || 0), 0);
  const baseAmount  = totalAmount * exchangeRate;

  // ── Document state ─────────────────────────────────────────────────────────
  const [deposit,   setDeposit]   = useState<BankDepositDto | null>(null);
  const [loading,   setLoading]   = useState(!isNew);
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState<string | null>(null);
  const [success,   setSuccess]   = useState<string | null>(null);
  const [tab,       setTab]       = useState<Tab>('lines');
  const [modal,     setModal]     = useState<'approve' | 'reject' | 'reverse' | 'cancel' | 'delete' | null>(null);
  const [printOpen, setPrintOpen] = useState(false);

  useEffect(() => {
    if (!isNew) return;
    setCostCenterId(session?.costCenterId ?? '');
  }, [isNew, session?.costCenterId]);

  // ── Arriving from a customer receipt: its cheques in hand become the lines (424365) ──
  const [searchParams] = useSearchParams();
  const fromReceipt = isNew ? searchParams.get('receipt') : null;
  useEffect(() => {
    if (!fromReceipt) return;
    chequeApi.inHandForReference(fromReceipt).then(cheques => {
      if (cheques.length === 0) return;
      setDepositType('Cheque');
      setReference(fromReceipt);
      setLines(cheques.map((c, i) => ({
        ...mkLine(i + 1),
        description:          `Cheque from receipt ${fromReceipt}`,
        paymentMethod:        'Cheque',
        sourceDocumentNumber: fromReceipt,
        chequeRecordId:       c.id,
        chequeNumber:         c.chequeNumber,
        chequeDate:           c.chequeDate ? c.chequeDate.split('T')[0] : '',
        customerCode:         c.customerCode ?? '',
        amount:               c.amount,
      })));
    }).catch(() => { /* the deposit can still be filled in by hand */ });
  }, [fromReceipt]);

  useEffect(() => {
    if (!currencyCode || currencyCode === baseCurrency) {
      if (isNew) setExchangeRate(1);
      return;
    }
    exchangeRateApi.getList({ fromCurrencyCode: baseCurrency, toCurrencyCode: currencyCode, pageSize: 10 })
      .then(res => {
        const items = res.data?.data?.items ?? [];
        const sorted = items
          .filter((r: { effectiveDate: string }) => r.effectiveDate <= depositDate)
          .sort((a: { effectiveDate: string }, b: { effectiveDate: string }) => b.effectiveDate.localeCompare(a.effectiveDate));
        if (sorted.length > 0) setExchangeRate(sorted[0].rate);
        else if (items.length > 0) setExchangeRate(items[0].rate);
      }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currencyCode, depositDate]);

  const fetchBankAccounts = async (search: string, page: number, pageSize: number) => {
    const res = await bankAccountApi.getList({ search, pageNumber: page, pageSize });
    const d = res.data.data;
    return { items: d?.items ?? [], totalCount: d?.totalCount ?? 0 };
  };

  const fetchGlAccounts = async (search: string, page: number, pageSize: number) => {
    const res = await accountApi.getList({ search, pageNumber: page, pageSize, isActive: true } as Parameters<typeof accountApi.getList>[0]);
    const d = res.data.data;
    return { items: d?.items ?? [], totalCount: d?.totalCount ?? 0 };
  };

  // ── Load ─────────────────────────────────────────────────────────────────
  const loadDeposit = useCallback(async () => {
    if (isNew || !id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await bankDepositApi.getById(id);
      const d = res.data?.data;
      if (!d) throw new Error('Deposit not found');
      setDeposit(d);

      setDepositDate(d.depositDate.split('T')[0]);
      setPostingDate(d.postingDate.split('T')[0]);
      setBankAccountId(d.bankAccountId);
      setBankAccountCode(d.bankAccountCode);
      setBankAccountName(d.bankAccountName);
      setDepositType(d.depositType);
      setCurrencyCode(d.currencyCode);
      setExchangeRate(d.exchangeRate);
      setDescription(d.narration ?? '');
      setReference(d.reference ?? '');
      setCostCenterId(d.costCenterId ?? '');

      bankAccountApi.getById(d.bankAccountId)
        .then(r => { if (r.data?.data) setBankAccountNumber(r.data.data.accountNumber); })
        .catch(() => {});

      setLines(d.lines.map(l => ({
        key:                 l.id,
        lineNumber:          l.lineNumber,
        description:         l.remarks ?? '',
        depositSource:       l.depositSource ?? '',
        sourceDocumentNumber: l.sourceDocumentNumber ?? '',
        sourceGlAccountId:   l.sourceGlAccountId ?? '',
        sourceGlAccountCode: l.sourceGlAccountCode ?? '',
        sourceGlAccountName: l.sourceGlAccountName ?? '',
        paymentMethod:       l.paymentMethod,
        chequeNumber:        l.chequeNumber ?? '',
        chequeRecordId:      l.chequeRecordId ?? '',
        chequeDate:          l.chequeDate ? l.chequeDate.split('T')[0] : '',
        customerId:          l.customerId ?? '',
        customerCode:        l.customerCode ?? '',
        amount:              l.amount,
        sourceLocationId:    l.sourceLocationId ?? '',
        sourceBusinessDate:  l.sourceBusinessDate ? l.sourceBusinessDate.split('T')[0] : '',
        sourceLocationCode:  '',
        sourceLocationName:  '',
        sourceCostCenterId:  l.sourceCostCenterId ?? '',
      })));

      dispatch(setErpContext({
        companyCode:         authUser?.companyId?.substring(0, 8).toUpperCase() ?? 'COMPANY',
        companyName:         '',
        locationCode:        null,
        locationName:        null,
        costCenterCode:      null,
        costCenterName:      null,
        financialYearCode:   d.financialYearCode,
        financialPeriodCode: d.financialPeriodCode,
        transactionTypeCode: 'BNK-DEP',
        documentNumber:      d.depositNumber,
        currencyCode:        d.currencyCode,
        exchangeRate:        d.exchangeRate,
      }));
    } catch (err: unknown) {
      setError((err as Error).message ?? 'Failed to load deposit');
    } finally {
      setLoading(false);
    }
  }, [id, isNew, dispatch, authUser]);

  useEffect(() => { loadDeposit(); }, [loadDeposit]);
  useEffect(() => { return () => { dispatch(clearErpContext()); }; }, [dispatch]);

  const status = (deposit?.workflowStatus ?? 'Draft') as BankDepositWorkflowStatus;
  const isDraftEditable = isNew || status === 'Draft' || status === 'Rejected';
  const isEditable = isDraftEditable && (isNew ? hasPermission(Permissions.BNK_DEPOSIT_CREATE) : canEdit);

  // ── Build lines payload ────────────────────────────────────────────────────
  const buildLines = (): BankDepositLineRequest[] =>
    lines.filter(l => l.amount > 0).map((l, idx) => ({
      lineNumber:           idx + 1,
      depositSource:        l.depositSource || null,
      sourceDocumentNumber: l.sourceDocumentNumber || null,
      sourceGlAccountId:    l.sourceGlAccountId || null,
      paymentMethod:        l.paymentMethod,
      chequeNumber:         l.chequeNumber || null,
      chequeDate:           l.chequeDate || null,
      chequeRecordId:       l.chequeRecordId || null,
      customerId:           l.customerId || null,
      customerCode:         l.customerCode || null,
      amount:               l.amount,
      currencyCode,
      exchangeRate,
      remarks:              l.description || null,
      sourceLocationId:     l.sourceLocationId || null,
      sourceBusinessDate:   l.sourceBusinessDate || null,
      sourceCostCenterId:   l.sourceCostCenterId || null,
    }));

  // ── Save ───────────────────────────────────────────────────────────────────
  // ── Cash in hand check (424363) ─────────────────────────────────────────────
  // Each cash line draws from a till account; its posted balance at the line's location up to the
  // posting date is the cash in hand. Depositing a different amount is allowed (a float kept back,
  // a shortage being investigated) but never silently: the line says so and Save asks once.
  const [cashInHand, setCashInHand]   = useState<Record<string, number>>({});
  const [cashWarning, setCashWarning] = useState<string[] | null>(null);
  const cashKey = (l: DepositLineRow) =>
    `${l.sourceGlAccountId}|${l.sourceLocationId || session?.locationId || ''}|${postingDate}`;

  useEffect(() => {
    if (!isEditable) return;
    const keys = [...new Set(lines.filter(l => l.paymentMethod === 'Cash' && l.sourceGlAccountId).map(cashKey))];
    keys.filter(k => !(k in cashInHand)).forEach(k => {
      const [accountId, locationId, asOf] = k.split('|');
      bankDepositApi.getCashInHand({ accountId, locationId: locationId || undefined, asOf })
        .then(r => setCashInHand(c => ({ ...c, [k]: r.data?.data?.balance ?? 0 })))
        .catch(() => { /* no figure, no warning — the deposit itself is unaffected */ });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines, postingDate, isEditable, session?.locationId]);

  /** Per till account (and location): what this deposit takes from it vs what is in hand. */
  const cashGaps = Object.entries(
    lines.filter(l => l.paymentMethod === 'Cash' && l.sourceGlAccountId)
      .reduce<Record<string, { label: string; amount: number }>>((acc, l) => {
        const k = cashKey(l);
        acc[k] = { label: l.sourceGlAccountCode || 'cash account', amount: (acc[k]?.amount ?? 0) + (l.amount || 0) };
        return acc;
      }, {}))
    .filter(([k]) => k in cashInHand)
    .map(([k, v]) => ({ key: k, label: v.label, amount: v.amount, inHand: cashInHand[k], gap: Math.round((v.amount - cashInHand[k]) * 100) / 100 }))
    .filter(g => g.amount > 0 && g.gap !== 0);
  const money = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const gapText = (g: { label: string; amount: number; inHand: number; gap: number }) =>
    `${g.label}: depositing ${money(g.amount)} but cash in hand is ${money(g.inHand)} — ${money(Math.abs(g.gap))} ${g.gap < 0 ? 'less' : 'more'} than cash in hand.`;

  const handleSave = async (confirmedCash = false) => {
    setError(null);
    if (!confirmedCash && cashGaps.length > 0) { setCashWarning(cashGaps.map(gapText)); return; }
    setCashWarning(null);
    if (!bankAccountId) { setError('Please select a bank account.'); return; }
    const linesPayload = buildLines();
    if (linesPayload.length === 0) { setError('At least one deposit line with amount > 0 is required.'); return; }

    setSaving(true);
    try {
      if (isNew) {
        const payload: CreateBankDepositRequest = {
          companyId:           authUser?.companyId ?? '',
          locationId:          null,
          costCenterId:        costCenterId || null,
          financialYearCode:   session?.financialYearCode ?? '',
          financialPeriodCode: session?.financialPeriodCode ?? '',
          depositDate,
          postingDate,
          bankAccountId,
          depositType,
          currencyCode,
          exchangeRate,
          reference:  reference || null,
          narration:  description || null,
          lines:      linesPayload,
        };
        const res = await bankDepositApi.create(payload);
        const newId = res.data?.data?.id;
        setSuccess('Deposit created successfully.');
        setTimeout(() => navigate(`/banking/deposits/${newId}`), 800);
      } else if (deposit) {
        const payload: UpdateBankDepositRequest = {
          // Kept as loaded: leaving these out cleared the deposit's location and cost centre on
          // every edit, so the posting lost where the cash came from.
          locationId:   deposit.locationId ?? null,
          costCenterId: costCenterId || null,
          depositDate,
          postingDate,
          bankAccountId,
          bankAccountCode,
          bankAccountName,
          bankGLAccountId: null,
          depositType,
          currencyCode,
          exchangeRate,
          reference:  reference || null,
          narration:  description || null,
          lines:      linesPayload,
        };
        await bankDepositApi.update(deposit.id, payload);
        setSuccess('Deposit updated.');
        setTimeout(() => { setSuccess(null); loadDeposit(); }, 800);
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Failed to save deposit.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  // ── Workflow ───────────────────────────────────────────────────────────────
  const doAction = async (action: () => Promise<void>, successMsg: string) => {
    setError(null); setSaving(true);
    try {
      await action();
      setSuccess(successMsg);
      setTimeout(() => { setSuccess(null); loadDeposit(); }, 1200);
    } catch (err: unknown) {
      setError((err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Action failed.');
    } finally { setSaving(false); }
  };

  const handleSubmit  = () => doAction(async () => { await bankDepositApi.submit(deposit!.id); }, 'Deposit submitted for approval.');
  // When every line names the cash account it came from, posting credits those accounts and needs
  // no contra account (the server ignores one then).
  const linesNameTheirSource = !!deposit && deposit.lines.length > 0 && deposit.lines.every(l => !!l.sourceGlAccountId);
  const handlePost    = () => {
    if (!contraAccountId && !linesNameTheirSource) { setError('Please select a contra GL account before posting.'); return; }
    doAction(async () => { await bankDepositApi.post(deposit!.id, contraAccountId || null); }, 'Deposit posted to general ledger.');
  };
  const handleApprove = (r: string) => doAction(async () => { await bankDepositApi.approve(deposit!.id, r); }, 'Deposit approved.');
  const handleReject  = (r: string) => doAction(async () => { await bankDepositApi.reject(deposit!.id, r); }, 'Deposit rejected.');
  const handleCancel  = (r: string) => doAction(async () => { await bankDepositApi.cancel(deposit!.id, r); }, 'Deposit cancelled.');
  const handleReverse = (narr: string) => doAction(async () => {
    const res = await bankDepositApi.reverse(deposit!.id, today, today, narr || undefined);
    const revId = res.data?.data?.id;
    setTimeout(() => navigate(`/banking/deposits/${revId}`), 1200);
  }, 'Reversal deposit created.');

  const handleDelete = async () => {
    if (!deposit) return;
    setSaving(true); setError(null);
    try {
      await bankDepositApi.delete(deposit.id);
      navigate(AppRoutes.BANK_DEPOSITS, { replace: true });
    } catch (e: unknown) {
      setError((e as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message
        ?? (e as { message?: string })?.message ?? 'Delete failed.');
    } finally { setSaving(false); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full"><Spinner size="lg" /></div>;
  }

  const today2 = new Date().toISOString().split('T')[0];

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900 overflow-hidden">
      <ERPContextBar />

      {/* Toolbar */}
      <div className="flex-shrink-0 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-4 py-2.5">
        <div className="flex items-center gap-2 flex-wrap">

          <button onClick={() => navigate(AppRoutes.BANK_DEPOSITS)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 dark:text-slate-400
                       hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700
                       rounded-lg transition-colors">
            <ArrowLeft size={14} /> Back
          </button>
          <div className="w-px h-6 bg-slate-200 dark:bg-slate-600" />

          {isEditable && (
            <button onClick={() => void handleSave()} disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-teal-600 hover:bg-teal-700
                         text-white rounded-lg disabled:opacity-50 transition-colors font-medium">
              {saving ? <Spinner size="sm" /> : <Save size={14} />} Save Draft
            </button>
          )}
          {!isNew && status === 'Draft' && canSubmit && (
            <button onClick={handleSubmit} disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-sky-600 hover:bg-sky-700
                         text-white rounded-lg disabled:opacity-50 transition-colors font-medium">
              <Send size={14} /> Submit for Approval
            </button>
          )}
          {!isNew && (status === 'Submitted' || status === 'PendingApproval') && canApprove && (
            <button onClick={() => setModal('approve')} disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-600 hover:bg-green-700
                         text-white rounded-lg disabled:opacity-50 transition-colors font-medium">
              <CheckCircle2 size={14} /> Approve
            </button>
          )}
          {!isNew && (status === 'Submitted' || status === 'PendingApproval') && canReject && (
            <button onClick={() => setModal('reject')} disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-red-600 hover:bg-red-700
                         text-white rounded-lg disabled:opacity-50 transition-colors font-medium">
              <XCircle size={14} /> Reject
            </button>
          )}
          {!isNew && status === 'Approved' && canPost && (
            <button onClick={handlePost} disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-emerald-600 hover:bg-emerald-700
                         text-white rounded-lg disabled:opacity-50 transition-colors font-medium">
              <BookCheck size={14} /> Post to Ledger
            </button>
          )}
          {!isNew && status === 'Posted' && canReverse && !deposit?.reversalDepositId && (
            <button onClick={() => setModal('reverse')} disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-amber-600 hover:bg-amber-700
                         text-white rounded-lg disabled:opacity-50 transition-colors font-medium">
              <RotateCcw size={14} /> Reverse
            </button>
          )}
          {!isNew && !['Posted', 'Cancelled', 'Reversed'].includes(status) && canCancel && (
            <button onClick={() => setModal('cancel')} disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-rose-600 hover:bg-rose-700
                         text-white rounded-lg disabled:opacity-50 transition-colors font-medium">
              <Ban size={14} /> Cancel
            </button>
          )}
          {!isNew && status === 'Draft' && canDelete && (
            <button onClick={() => setModal('delete')} disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-red-600 hover:bg-red-700
                         text-white rounded-lg disabled:opacity-50 transition-colors font-medium">
              <Trash2 size={14} /> Delete
            </button>
          )}
          {!isNew && deposit && canPrint && (
            <button onClick={() => setPrintOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 dark:text-slate-400
                         hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
              <Printer size={14} /> Print
            </button>
          )}
          {!isNew && (
            <div className="ml-auto flex items-center gap-2">
              {deposit?.isReversal && (
                <span className="px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-xs font-semibold rounded-full">Reversal</span>
              )}
              <span className={cn('px-3 py-1 rounded-full text-xs font-semibold',
                BNK_DEPOSIT_WORKFLOW_STATUS_COLORS[status] ?? 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-gray-400')}>
                {status}
              </span>
            </div>
          )}
        </div>

        {cashWarning && (
          <div className="mt-2 px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-sm text-amber-800 dark:text-amber-300">
            <div className="flex items-start gap-2">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-medium">The deposit does not match the cash in hand.</div>
                {cashWarning.map(w => <div key={w}>{w}</div>)}
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-2">
              <button onClick={() => setCashWarning(null)}
                className="px-3 py-1 text-xs font-medium rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/40">Go back and correct</button>
              <button onClick={() => void handleSave(true)}
                className="px-3 py-1 text-xs font-semibold rounded-lg bg-amber-600 hover:bg-amber-700 text-white">Save anyway</button>
            </div>
          </div>
        )}
        {error && (
          <div className="mt-2 flex items-start gap-2 px-3 py-2 bg-red-50 dark:bg-red-900/20
                          border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
            <AlertTriangle size={14} className="shrink-0 mt-0.5" /><span>{error}</span>
          </div>
        )}
        {success && (
          <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-green-50 dark:bg-green-900/20
                          border border-green-200 dark:border-green-800 rounded-lg text-sm text-green-700 dark:text-green-400">
            <CheckCircle size={14} className="shrink-0" /><span>{success}</span>
          </div>
        )}
      </div>

      {/* Header fields */}
      <div className="flex-shrink-0 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-2">
              <Landmark size={18} className="text-teal-600" />
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                {isNew ? 'New Bank Deposit' : `Bank Deposit ${deposit?.depositNumber ?? ''}`}
              </h2>
            </div>
            {!isNew && deposit && (
              <p className="text-xs text-slate-400 mt-0.5">Created {new Date(deposit.createdDate).toLocaleString()}</p>
            )}
          </div>
          <div className="flex items-center gap-3">
            {!isNew && deposit?.originalDepositId && (
              <button onClick={() => navigate(`/banking/deposits/${deposit.originalDepositId}`)}
                className="text-xs text-teal-600 hover:underline flex items-center gap-1">
                View Original <ExternalLink size={10} />
              </button>
            )}
            {!isNew && deposit?.reversalDepositId && (
              <button onClick={() => navigate(`/banking/deposits/${deposit.reversalDepositId}`)}
                className="text-xs text-amber-600 hover:underline flex items-center gap-1">
                View Reversal <ExternalLink size={10} />
              </button>
            )}
            {!isNew && deposit?.sourceGlJournalId && (
              <button onClick={() => navigate(`/gl/journals/${deposit.sourceGlJournalId}`)}
                className="text-xs text-emerald-600 hover:underline flex items-center gap-1">
                View GL Journal <ExternalLink size={10} />
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {/* Transaction Date */}
          <div>
            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
              Transaction Date <span className="text-red-500">*</span>
            </label>
            <input type="date" value={depositDate} onChange={e => setDepositDate(e.target.value)}
              disabled={!isEditable} className={fieldClass(!isEditable)} />
          </div>

          {/* Posting Date */}
          <div>
            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
              Posting Date <span className="text-red-500">*</span>
            </label>
            <input type="date" value={postingDate} onChange={e => setPostingDate(e.target.value)}
              disabled={!isEditable} className={fieldClass(!isEditable)} />
          </div>

          {/* Bank Account */}
          <div className="col-span-2">
            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
              Bank Account <span className="text-red-500">*</span>
            </label>
            <div
              onClick={() => isEditable && setBankOpen(true)}
              className={cn(
                'flex items-center gap-2 h-9 px-3 rounded-lg border text-sm transition-colors',
                'bg-white dark:bg-slate-700 border-slate-200 dark:border-slate-600',
                isEditable ? 'cursor-pointer hover:border-teal-400' : 'cursor-default',
                !bankAccountId && 'text-slate-400',
              )}
            >
              {bankAccountId ? (
                <>
                  <span className="font-mono text-xs font-semibold text-teal-700 dark:text-teal-400">{bankAccountNumber || bankAccountCode}</span>
                  <span className="text-slate-700 dark:text-slate-300 truncate flex-1 text-xs">{bankAccountName}</span>
                  {isEditable && (
                    <button type="button" onClick={e => { e.stopPropagation(); setBankAccountId(''); setBankAccountNumber(''); setBankAccountName(''); setBankAccountCode(''); }}
                      className="text-slate-300 hover:text-red-500"><X size={12} /></button>
                  )}
                </>
              ) : (
                <>
                  {isEditable && <Search size={12} className="text-slate-300" />}
                  <span className="text-xs italic">{isEditable ? 'Select bank account…' : '—'}</span>
                </>
              )}
            </div>
          </div>

          {/* Contra Account (GL) — required for posting */}
          <div className="col-span-2">
            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
              Contra GL Account <span className="text-red-500">*</span>
            </label>
            <div
              onClick={() => isEditable && setContraOpen(true)}
              className={cn(
                'flex items-center gap-2 h-9 px-3 rounded-lg border text-sm transition-colors',
                'bg-white dark:bg-slate-700 border-slate-200 dark:border-slate-600',
                isEditable ? 'cursor-pointer hover:border-teal-400' : 'cursor-default',
                !contraAccountId && 'text-slate-400',
              )}
            >
              {contraAccountId ? (
                <>
                  <span className="font-mono text-xs font-semibold text-teal-700 dark:text-teal-400">{contraAccountCode}</span>
                  <span className="text-slate-700 dark:text-slate-300 truncate flex-1 text-xs">{contraAccountName}</span>
                  {isEditable && (
                    <button type="button" onClick={e => { e.stopPropagation(); setContraAccountId(''); setContraAccountCode(''); setContraAccountName(''); }}
                      className="text-slate-300 hover:text-red-500"><X size={12} /></button>
                  )}
                </>
              ) : (
                <>
                  {isEditable && <Search size={12} className="text-slate-300" />}
                  <span className="text-xs italic">{isEditable ? 'Select contra GL account…' : '—'}</span>
                </>
              )}
            </div>
          </div>

          {/* Deposit Type */}
          <div>
            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Deposit Type</label>
            <select value={depositType} onChange={e => setDepositType(e.target.value as BankDepositType)}
              disabled={!isEditable} className={fieldClass(!isEditable)}>
              {DEPOSIT_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          {/* Currency */}
          <div>
            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Currency</label>
            <input value={currencyCode} onChange={e => setCurrencyCode(e.target.value.toUpperCase())}
              disabled={!isEditable} maxLength={10}
              className={cn(fieldClass(!isEditable), 'font-mono uppercase')} />
          </div>

          {/* Exchange Rate */}
          <div>
            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Exchange Rate</label>
            <input type="number" value={exchangeRate} onChange={e => setExchangeRate(parseFloat(e.target.value) || 1)}
              disabled={!isEditable} step="0.000001" min="0.000001" className={fieldClass(!isEditable)} />
          </div>

          {/* Reference */}
          <div>
            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Reference</label>
            <input value={reference} onChange={e => setReference(e.target.value)}
              disabled={!isEditable} placeholder="Optional" className={fieldClass(!isEditable)} />
          </div>

          {/* Cost Centre */}
          <div>
            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Cost Centre</label>
            <select value={costCenterId} onChange={e => setCostCenterId(e.target.value)}
              disabled={!isEditable} className={fieldClass(!isEditable)}>
              <option value="">— None —</option>
              {costCentresForLocation(accessibleCostCenters, session?.locationId).map(cc => <option key={cc.id} value={cc.id}>{cc.code} — {cc.name}</option>)}
            </select>
          </div>

          {/* Financial Period */}
          <div>
            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Financial Period</label>
            <div className={fieldClass(true)}>
              {deposit?.financialPeriodCode ?? session?.financialPeriodCode ?? '—'}
            </div>
          </div>

          {/* Description */}
          <div className="col-span-2 md:col-span-3 lg:col-span-6">
            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Description</label>
            <input value={description} onChange={e => setDescription(e.target.value)}
              disabled={!isEditable} placeholder="Describe this deposit…" className={fieldClass(!isEditable)} />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex-shrink-0 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6">
        <div className="flex">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={cn(
                'flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors',
                tab === t.key
                  ? 'border-teal-600 text-teal-600'
                  : 'border-transparent text-slate-500 dark:text-gray-400 hover:text-slate-700 dark:hover:text-slate-300 hover:border-slate-300',
              )}>
              <t.icon size={14} />{t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab body */}
      <div className="flex-1 overflow-auto">

        {/* Lines */}
        {tab === 'lines' && (
          <div className="flex flex-col h-full min-h-0">
            <div className="flex-1 overflow-auto">
              <table className="w-full text-sm border-collapse">
                <thead className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-800/90 backdrop-blur-sm
                                  border-b border-slate-200 dark:border-slate-700">
                  <tr>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide w-8">#</th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide min-w-[120px]">Description</th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide w-28">Deposit Source</th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide w-28">Source Doc #</th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide min-w-[190px]">Source Account</th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide w-40">Cash Due</th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide w-32">Payment Method</th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide w-28">Cheque #</th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide w-28">Cheque Date</th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide w-28">Customer</th>
                    <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide w-32">Amount</th>
                    {isEditable && <th className="w-9" />}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {lines.map((line, idx) => (
                    <tr key={line.key} className="group hover:bg-teal-50/40 dark:hover:bg-slate-700/30 transition-colors">
                      <td className="px-3 py-2 text-xs text-slate-400 select-none">{idx + 1}</td>

                      <td className="px-3 py-1.5">
                        <input value={line.description} onChange={e => updateLine(line.key, { description: e.target.value })}
                          disabled={!isEditable} placeholder="Description…"
                          className="w-full px-2 py-1 text-sm rounded border border-transparent
                                     hover:border-slate-200 focus:border-teal-400 focus:bg-white
                                     bg-transparent disabled:bg-transparent outline-none text-slate-700 dark:text-slate-300 placeholder-slate-300" />
                      </td>

                      <td className="px-3 py-1.5">
                        <input value={line.depositSource} onChange={e => updateLine(line.key, { depositSource: e.target.value })}
                          disabled={!isEditable} placeholder="Source…"
                          className="w-full px-2 py-1 text-sm rounded border border-transparent
                                     hover:border-slate-200 focus:border-teal-400 focus:bg-white
                                     bg-transparent disabled:bg-transparent outline-none text-slate-700 dark:text-slate-300 placeholder-slate-300" />
                      </td>

                      <td className="px-3 py-1.5">
                        <input value={line.sourceDocumentNumber} onChange={e => updateLine(line.key, { sourceDocumentNumber: e.target.value })}
                          disabled={!isEditable} placeholder="Doc #…"
                          className="w-full px-2 py-1 text-sm rounded border border-transparent
                                     hover:border-slate-200 focus:border-teal-400 focus:bg-white
                                     bg-transparent disabled:bg-transparent outline-none text-slate-700 dark:text-slate-300 placeholder-slate-300" />
                      </td>

                      <td className="px-3 py-1.5 min-w-[190px]">
                        <AccountLookupField label="" value={line.sourceGlAccountId || null}
                          displayCode={line.sourceGlAccountCode || null} displayName={line.sourceGlAccountName || null}
                          isPostingAccount disabled={!isEditable}
                          onChange={(aid, ac, an) => updateLine(line.key, {
                            sourceGlAccountId: aid ?? '', sourceGlAccountCode: ac ?? '', sourceGlAccountName: an ?? '',
                          })}
                          placeholder="Cash account…" />
                      </td>

                      {/* Which day's collection at which location this cash line settles
                          (GAP-016). Picking one caps the amount at what remains due for it. */}
                      <td className="px-3 py-1.5">
                        {line.paymentMethod === 'Cash' ? (
                          <div className="flex items-center gap-1">
                            {line.sourceLocationId ? (
                              <span className="text-xs text-slate-600 dark:text-slate-300 truncate">
                                {line.sourceLocationCode || '…'} · {line.sourceBusinessDate}
                              </span>
                            ) : (
                              <span className="text-xs text-slate-300 dark:text-slate-600 italic">Not linked</span>
                            )}
                            {isEditable && (
                              <button type="button" onClick={() => setDuePickerForLine(line.key)}
                                className="shrink-0 p-1 rounded text-slate-400 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20"
                                title="Pick a cash-due row">
                                <Search size={13} />
                              </button>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-300 dark:text-slate-600">—</span>
                        )}
                      </td>

                      <td className="px-3 py-1.5">
                        {isEditable ? (
                          <select value={line.paymentMethod}
                            onChange={e => updateLine(line.key, { paymentMethod: e.target.value })}
                            className="w-full px-2 py-1 text-sm rounded border border-transparent
                                       hover:border-slate-200 focus:border-teal-400 focus:bg-white
                                       bg-transparent outline-none text-slate-700 dark:text-slate-300">
                            {PAYMENT_METHOD_OPTIONS.map(m => <option key={m} value={m}>{m}</option>)}
                          </select>
                        ) : (
                          <span className="text-sm text-slate-700 dark:text-slate-300">{line.paymentMethod}</span>
                        )}
                      </td>

                      <td className="px-3 py-1.5">
                        {/* Pick a cheque from the register (GAP-016) so depositing moves it out of
                            hand — or type a number by hand, which stays a loose string as before. */}
                        <div className="flex items-center gap-1">
                          <input value={line.chequeNumber}
                            onChange={e => updateLine(line.key, { chequeNumber: e.target.value, chequeRecordId: '' })}
                            disabled={!isEditable || line.paymentMethod !== 'Cheque'}
                            placeholder={line.paymentMethod === 'Cheque' ? 'Cheque #…' : '—'}
                            className="w-full px-2 py-1 text-sm rounded border border-transparent
                                       hover:border-slate-200 focus:border-teal-400 focus:bg-white
                                       bg-transparent disabled:bg-transparent outline-none text-slate-700 dark:text-slate-300 placeholder-slate-300" />
                          {isEditable && line.paymentMethod === 'Cheque' && (
                            line.chequeRecordId ? (
                              <span title="Linked to the cheque register — depositing will move it out of hand"
                                    className="shrink-0 text-teal-600 dark:text-teal-400"><BookCheck size={14} /></span>
                            ) : (
                              <button type="button"
                                onClick={() => {
                                  if (!bankAccountId) { setError('Select a bank account first.'); return; }
                                  setPickerForLine(line.key);
                                }}
                                title="Pick a received cheque in hand"
                                className="shrink-0 p-1 rounded text-slate-400 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20">
                                <Search size={14} />
                              </button>
                            )
                          )}
                        </div>
                      </td>

                      <td className="px-3 py-1.5">
                        <input type="date" value={line.chequeDate} onChange={e => updateLine(line.key, { chequeDate: e.target.value })}
                          disabled={!isEditable || line.paymentMethod !== 'Cheque'}
                          className="w-full px-2 py-1 text-sm rounded border border-transparent
                                     hover:border-slate-200 focus:border-teal-400 focus:bg-white
                                     bg-transparent disabled:bg-transparent outline-none text-slate-700 dark:text-slate-300" />
                      </td>

                      <td className="px-3 py-1.5">
                        <input value={line.customerCode} onChange={e => updateLine(line.key, { customerCode: e.target.value })}
                          disabled={!isEditable} placeholder="Code…"
                          className="w-full px-2 py-1 text-sm rounded border border-transparent
                                     hover:border-slate-200 focus:border-teal-400 focus:bg-white
                                     bg-transparent disabled:bg-transparent outline-none text-slate-700 dark:text-slate-300 placeholder-slate-300" />
                      </td>

                      <td className="px-3 py-1.5">
                        <input type="number" value={line.amount || ''} onChange={e => updateLine(line.key, { amount: parseFloat(e.target.value) || 0 })}
                          disabled={!isEditable} min="0" step="0.01"
                          className="w-full px-2 py-1 text-sm text-right font-mono rounded border border-transparent
                                     hover:border-slate-200 focus:border-teal-400 focus:bg-white
                                     bg-transparent disabled:bg-transparent outline-none
                                     text-slate-900 dark:text-white font-semibold" />
                        {isEditable && line.paymentMethod === 'Cash' && cashKey(line) in cashInHand && (() => {
                          const gap = cashGaps.find(g => g.key === cashKey(line));
                          return (
                            <div className={`text-[11px] text-right mt-0.5 ${gap ? 'text-amber-600 dark:text-amber-400' : 'text-slate-400'}`}
                              title={gap ? gapText(gap) : undefined}>
                              In hand {money(cashInHand[cashKey(line)])}
                              {gap ? ` · ${money(Math.abs(gap.gap))} ${gap.gap < 0 ? 'short' : 'over'}` : ''}
                            </div>
                          );
                        })()}
                      </td>

                      {isEditable && (
                        <td className="px-2 py-1.5">
                          <button onClick={() => removeLine(line.key)} disabled={lines.length <= 1}
                            className="p-1 rounded text-slate-200 hover:text-red-500 hover:bg-red-50
                                       disabled:opacity-0 disabled:cursor-not-allowed
                                       opacity-0 group-hover:opacity-100 transition-all">
                            <Trash2 size={13} />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700">
              {isEditable && (
                <div className="px-4 py-2 border-b border-slate-100 dark:border-slate-700">
                  <button onClick={addLine}
                    className="flex items-center gap-1.5 text-sm text-teal-600 hover:text-teal-700
                               dark:text-teal-400 dark:hover:text-teal-300 font-medium">
                    <Plus size={14} /> Add Line
                  </button>
                </div>
              )}
              <div className="flex items-center justify-end gap-8 px-6 py-3 bg-slate-50 dark:bg-slate-800/80">
                <div className="text-right">
                  <p className="text-xs text-slate-500 dark:text-gray-400 mb-0.5">Total Amount ({currencyCode})</p>
                  <p className="text-base font-bold font-mono text-teal-700 dark:text-teal-400">{fmt(totalAmount)}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-slate-500 dark:text-gray-400 mb-0.5">Base Amount</p>
                  <p className="text-sm font-semibold font-mono text-slate-700 dark:text-slate-300">{fmt(baseAmount)}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === 'workflow' && (
          <div className="p-6">
            {!isNew && deposit ? (
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
                <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-4">Approval Status</h3>
                <WorkflowStatusPanel entityName="BankDeposit" entityId={deposit.id} />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-48 text-slate-400">
                <History size={28} className="mb-2 opacity-30" />
                <p className="text-sm">Save the deposit first to track workflow.</p>
              </div>
            )}
          </div>
        )}

        {tab === 'attachments' && (
          <div className="p-6">
            {!isNew && deposit ? (
              <AttachmentPanel entityName="BankDeposit" entityId={deposit.id} canAdd={canAddDoc} canDelete={canDelDoc} />
            ) : (
              <div className="flex flex-col items-center justify-center h-48 text-slate-400">
                <Paperclip size={28} className="mb-2 opacity-30" />
                <p className="text-sm">Save the deposit first to add attachments.</p>
              </div>
            )}
          </div>
        )}

        {tab === 'audit' && (
          <div className="p-6">
            {!isNew && deposit ? (
              <AuditViewer entityName="BankDeposit" entityId={deposit.id} />
            ) : (
              <div className="flex flex-col items-center justify-center h-48 text-slate-400">
                <ShieldCheck size={28} className="mb-2 opacity-30" />
                <p className="text-sm">Save the deposit first to view audit history.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Action modals */}
      {modal === 'approve' && (
        <ActionModal title="Approve Deposit" placeholder="Approval remarks (optional)…" required={false}
          onConfirm={text => { setModal(null); handleApprove(text); }} onCancel={() => setModal(null)} />
      )}
      {modal === 'reject' && (
        <ActionModal title="Reject Deposit" placeholder="Rejection reason (required)…" required={true}
          onConfirm={text => { setModal(null); handleReject(text); }} onCancel={() => setModal(null)} />
      )}
      {modal === 'reverse' && (
        <ActionModal title="Reverse Deposit" placeholder="Reversal narration (optional)…" required={false}
          onConfirm={text => { setModal(null); handleReverse(text); }} onCancel={() => setModal(null)} />
      )}
      {modal === 'cancel' && (
        <ActionModal title="Cancel Deposit" placeholder="Cancellation reason (required)…" required={true}
          onConfirm={text => { setModal(null); handleCancel(text); }} onCancel={() => setModal(null)} />
      )}
      {modal === 'delete' && (
        <ActionModal title="Delete Draft Deposit"
          placeholder="The draft is cancelled and kept for audit. It can no longer be edited or posted."
          required={false}
          onConfirm={() => { setModal(null); handleDelete(); }}
          onCancel={() => setModal(null)} />
      )}

      <MasterSearchModal<BankAccountListDto>
        isOpen={bankOpen}
        onClose={() => setBankOpen(false)}
        title="Select Bank Account"
        columns={BANK_ACCOUNT_COLUMNS}
        fetchData={fetchBankAccounts}
        onSelect={item => {
          setBankAccountId(item.id);
          setBankAccountNumber(item.accountNumber);
          setBankAccountName(item.accountName);
          setBankAccountCode(item.accountNumber);
          if (item.currencyCode) setCurrencyCode(item.currencyCode);
          setBankOpen(false);
        }}
      />

      <MasterSearchModal<AccountListDto>
        isOpen={contraOpen}
        onClose={() => setContraOpen(false)}
        title="Select Contra (GL) Account"
        columns={GL_ACCOUNT_COLUMNS}
        fetchData={fetchGlAccounts}
        onSelect={item => {
          setContraAccountId(item.id);
          setContraAccountCode(item.code);
          setContraAccountName(item.name);
          setContraOpen(false);
        }}
      />

      {deposit && (
        <MasterReportPreview
          isOpen={printOpen}
          onClose={() => setPrintOpen(false)}
          title={`Bank Deposit ${deposit.depositNumber}`}
          fileBaseName={`Bank-Deposit-${deposit.depositNumber}`}
          email={{ documentType: 'Bank Deposit', documentNo: deposit.depositNumber }}
          onDownloadExcel={() => exportToExcel({
            sheetName: 'Bank Deposit',
            title: `Bank Deposit — ${deposit.depositNumber}`,
            subtitle: `${deposit.bankAccountName ?? ''} · ${deposit.currencyCode ?? ''}`,
            columns: [
              { header: '#',              key: 'lineNumber',    width: 6,  align: 'center' },
              { header: 'Description',    key: 'description',   width: 30 },
              { header: 'Source',         key: 'depositSource', width: 16 },
              { header: 'Payment Method', key: 'paymentMethod', width: 16 },
              { header: 'Cheque #',       key: 'chequeNumber',  width: 16 },
              { header: 'Cheque Date',    key: 'chequeDate',    width: 14 },
              { header: 'Customer',       key: 'customerCode',  width: 16 },
              { header: 'Amount',         key: 'amount',        width: 16, numFmt: '#,##0.00', align: 'right' },
            ],
            rows: (deposit.lines ?? []).map(l => ({
              lineNumber:    l.lineNumber,
              description:   l.remarks ?? '',
              depositSource: l.depositSource ?? '',
              paymentMethod: l.paymentMethod ?? '',
              chequeNumber:  l.chequeNumber ?? '',
              chequeDate:    l.chequeDate ? l.chequeDate.slice(0, 10) : '',
              customerCode:  l.customerCode ?? '',
              amount:        l.amount ?? 0,
            })),
            totalsRow: { description: 'Total', amount: deposit.totalAmount ?? 0 },
          }, `Bank-Deposit-${deposit.depositNumber}`)}
        >
          <BankDepositVoucher
            deposit={deposit}
            companyName={authUser?.companyId ? '' : 'myLedger ONE'}
            printedBy={authUser?.username}
          />
        </MasterReportPreview>
      )}

      {pickerForLine && (
        <ChequeInHandPicker
          bankAccountId={bankAccountId}
          onClose={() => setPickerForLine(null)}
          onPick={(c) => {
            updateLine(pickerForLine, {
              chequeRecordId: c.id,
              chequeNumber:   c.chequeNumber,
              chequeDate:     c.chequeDate ? c.chequeDate.split('T')[0] : '',
              customerCode:   c.customerCode ?? '',
              amount:         c.amount,
            });
            setPickerForLine(null);
          }}
        />
      )}

      {duePickerForLine && (
        <CashDuePicker
          onClose={() => setDuePickerForLine(null)}
          onPick={(row) => {
            updateLine(duePickerForLine, {
              sourceLocationId:   row.locationId,
              sourceBusinessDate: row.businessDate.split('T')[0],
              sourceLocationCode: row.locationCode,
              sourceLocationName: row.locationName,
              amount:             row.remainingAmount,
            });
            setDuePickerForLine(null);
          }}
        />
      )}

      {/* Suppress unused variable warning */}
      {today2 && null}
    </div>
  );
};

// ── Pick a received cheque in hand to deposit (GAP-016) ──────────────────────

function ChequeInHandPicker({
  bankAccountId, onClose, onPick,
}: {
  bankAccountId: string;
  onClose: () => void;
  onPick: (cheque: ChequeRecordListDto) => void;
}) {
  const [cheques, setCheques] = useState<ChequeRecordListDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);
  const [search,  setSearch]  = useState('');

  useEffect(() => {
    chequeApi.inHandForDeposit(bankAccountId)
      .then(r => setCheques(r.data.data?.items ?? []))
      .catch(() => setError('Could not load cheques in hand.'))
      .finally(() => setLoading(false));
  }, [bankAccountId]);

  const shown = cheques.filter(c =>
    !search ||
    c.chequeNumber.toLowerCase().includes(search.toLowerCase()) ||
    (c.customerName ?? '').toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-2xl rounded-lg bg-white dark:bg-slate-900 shadow-xl max-h-[85vh] flex flex-col"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
          <div>
            <h3 className="font-semibold text-slate-800 dark:text-slate-100">Cheques in hand</h3>
            <p className="text-xs text-slate-500 dark:text-gray-400 mt-0.5">
              Received cheques not yet deposited for this bank account. Picking one links it, so
              posting the deposit moves it out of hand.
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" aria-label="Close">
            <X size={16} />
          </button>
        </div>
        <div className="p-3 border-b border-slate-100 dark:border-slate-800">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} autoFocus
              placeholder="Search by cheque number or customer…"
              className="w-full pl-8 pr-3 py-1.5 text-sm rounded border border-slate-300 dark:border-slate-600 dark:bg-slate-800" />
          </div>
        </div>
        <div className="overflow-auto p-2">
          {loading ? (
            <div className="py-10 text-center text-sm text-slate-400">Loading…</div>
          ) : error ? (
            <div className="m-2 flex items-start gap-2 rounded border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700 dark:bg-rose-900/20 dark:text-rose-400">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" /> {error}
            </div>
          ) : shown.length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-400">
              No received cheques in hand for this bank account.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-slate-500 dark:text-gray-400">
                <tr>
                  <th className="px-3 py-1.5 text-left">Cheque #</th>
                  <th className="px-3 py-1.5 text-left">Date</th>
                  <th className="px-3 py-1.5 text-left">Customer</th>
                  <th className="px-3 py-1.5 text-right">Amount</th>
                  <th className="px-3 py-1.5" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {shown.map(c => (
                  <tr key={c.id} className="hover:bg-teal-50/50 dark:hover:bg-teal-900/10">
                    <td className="px-3 py-1.5 font-mono text-slate-700 dark:text-slate-200">{c.chequeNumber}</td>
                    <td className="px-3 py-1.5 text-slate-500 dark:text-gray-400">{c.chequeDate?.split('T')[0] ?? '—'}</td>
                    <td className="px-3 py-1.5 text-slate-700 dark:text-slate-300">{c.customerName ?? '—'}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">
                      {c.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })} {c.currencyCode}
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      <button type="button" onClick={() => onPick(c)}
                        className="px-2 py-1 rounded text-xs font-medium bg-teal-600 text-white hover:bg-teal-700">
                        Pick
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Pick a day's cash collection still due for deposit (GAP-016) ─────────────

function CashDuePicker({
  onClose, onPick,
}: {
  onClose: () => void;
  onPick: (row: CashDueRowDto) => void;
}) {
  const [rows,    setRows]    = useState<CashDueRowDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);
  const [search,  setSearch]  = useState('');

  useEffect(() => {
    bankDepositApi.getCashDue()
      .then(r => setRows(r.data.data ?? []))
      .catch(() => setError('Could not load cash due.'))
      .finally(() => setLoading(false));
  }, []);

  const shown = rows.filter(r =>
    !search || r.locationCode.toLowerCase().includes(search.toLowerCase())
            || r.locationName.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-2xl rounded-lg bg-white dark:bg-slate-900 shadow-xl max-h-[85vh] flex flex-col"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
          <div>
            <h3 className="font-semibold text-slate-800 dark:text-slate-100">Cash due for deposit</h3>
            <p className="text-xs text-slate-500 dark:text-gray-400 mt-0.5">
              What each location has collected and not yet banked, by day. Picking a row fills the
              amount with what remains — a partial deposit is fine, the rest stays due.
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" aria-label="Close">
            <X size={16} />
          </button>
        </div>
        <div className="p-3 border-b border-slate-100 dark:border-slate-800">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} autoFocus
              placeholder="Search by location…"
              className="w-full pl-8 pr-3 py-1.5 text-sm rounded border border-slate-300 dark:border-slate-600 dark:bg-slate-800" />
          </div>
        </div>
        <div className="overflow-auto p-2">
          {loading ? (
            <div className="py-10 text-center text-sm text-slate-400">Loading…</div>
          ) : error ? (
            <div className="m-2 flex items-start gap-2 rounded border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700 dark:bg-rose-900/20 dark:text-rose-400">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" /> {error}
            </div>
          ) : shown.length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-400">
              Nothing is due — every location's collection has already been banked.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-slate-500 dark:text-gray-400">
                <tr>
                  <th className="px-3 py-1.5 text-left">Location</th>
                  <th className="px-3 py-1.5 text-left">Date</th>
                  <th className="px-3 py-1.5 text-right">Due</th>
                  <th className="px-3 py-1.5 text-right">Deposited</th>
                  <th className="px-3 py-1.5 text-right">Remaining</th>
                  <th className="px-3 py-1.5" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {shown.map(r => (
                  <tr key={`${r.locationId}-${r.businessDate}`} className="hover:bg-teal-50/50 dark:hover:bg-teal-900/10">
                    <td className="px-3 py-1.5 text-slate-700 dark:text-slate-300">
                      {r.locationCode} · {r.locationName}
                    </td>
                    <td className="px-3 py-1.5 text-slate-500 dark:text-gray-400">{r.businessDate.split('T')[0]}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-slate-500 dark:text-gray-400">
                      {r.dueAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-slate-500 dark:text-gray-400">
                      {r.depositedAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums font-medium text-slate-900 dark:text-white">
                      {r.remainingAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })} {r.currencyCode}
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      <button type="button" onClick={() => onPick(r)}
                        className="px-2 py-1 rounded text-xs font-medium bg-teal-600 text-white hover:bg-teal-700">
                        Pick
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
